<template>
  <div class="security-shifts">
    <h2 class="subheading grey--text">{{ $t('security_active_shifts') }}</h2>
    
    <v-container class="my-5">
      <!-- SHIFTS STATISTICS -->
      <v-layout row wrap class="mb-5" justify-space-around>
        <v-flex xs12 sm4 class="px-2 mb-3">
          <v-card class="elevation-2 rounded-lg pa-3 text-xs-center">
            <h3 class="grey--text text--darken-1">{{ $t('security_total_active_shifts') }}</h3>
            <div class="display-1 primary--text font-weight-bold mt-2">{{ totalShifts }}</div>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4 class="px-2 mb-3">
          <v-card class="elevation-2 rounded-lg pa-3 text-xs-center">
            <h3 class="grey--text text--darken-1">{{ $t('security_guards_on_duty') }}</h3>
            <div class="display-1 success--text font-weight-bold mt-2">{{ guardsOnDuty }}</div>
          </v-card>
        </v-flex>
        <v-flex xs12 sm4 class="px-2 mb-3">
          <v-card class="elevation-2 rounded-lg pa-3 text-xs-center">
            <h3 class="grey--text text--darken-1">{{ $t('security_locations_covered') }}</h3>
            <div class="display-1 info--text font-weight-bold mt-2">{{ locationsCovered }}</div>
          </v-card>
        </v-flex>
      </v-layout>

      <v-layout align-center justify-center fill-height>
          <v-btn color="primary" outline @click="fetchShifts" :loading="isLoading" round>
            <v-icon left>refresh</v-icon>
            {{ $t('refresh') }}
          </v-btn>
      </v-layout>

      <v-layout class="my-4" align-center justify-center fill-height>
        <v-flex xs12>
          <v-card-title class="px-0">
            <span class="title">{{ $t('security_shifts_list') }}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="$t('Buscar')"
              single-line
              hide-details
              color="primary"
            ></v-text-field>
          </v-card-title>

          <v-alert v-if="securityError" type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>

          <v-data-table
            :headers="shiftsHeaders"
            :items="shifts"
            :search="search"
            :loading="isLoading"
            :rows-per-page-text="$t('filas_por_pagina')"
            :rows-per-page-items="[
              {text: '5', value: 5},
              {text: '10', value: 10},
              {text: '25', value: 25},
              {text: this.$t('todos'), value: -1}
            ]"
            class="elevation-1"
          >
            <template slot="items" slot-scope="props">
              <td><strong>{{ props.item.guard_name }}</strong></td>
              <td>{{ props.item.company_name || 'N/A' }}</td>
              <td>
                <v-icon small color="grey" class="mr-1">access_time</v-icon>
                {{ formatTime(props.item.start_time) }}
              </td>
              <td>
                <v-icon small color="grey" class="mr-1">access_time</v-icon>
                {{ formatTime(props.item.end_time) }}
              </td>
              <td>
                <v-chip 
                  :color="props.item.is_active ? 'success' : 'warning'" 
                  text-color="white" 
                  small
                  class="font-weight-bold"
                >
                  <v-icon left size="16" v-if="props.item.is_active">check_circle</v-icon>
                  {{ props.item.is_active ? $t('active') : $t('inactive') }}
                </v-chip>
              </td>
              <td>
                <v-icon small color="info" class="mr-1">location_on</v-icon>
                {{ props.item.location || '-' }}
              </td>
            </template>
            <template v-slot:no-results>
               <v-alert :value="true" color="error" icon="warning">
                 {{$t('ningun_resultado_1')}} "{{ search }}" {{$t('ningun_resultado_2')}}
               </v-alert>
             </template>
             <template v-slot:pageText="props">
               {{props.rowsPerPage}}
               {{$t('turnos') || 'Turnos'}} {{ props.pageStart }} - {{ props.pageStop }} {{$t('prep_de')}} {{ props.itemsLength }}
             </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { FETCH_ACTIVE_SHIFTS } from '../../../store/modules/security'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityShifts',
  data() {
    return {
      pagination: {
        sortBy: 'start_time',
        descending: true,
        page: 1,
        rowsPerPage: 10
      },
      shiftsHeaders: [
        { text: 'Guardia', value: 'guard_name', sortable: true },
        { text: 'Empresa', value: 'company_name', sortable: false },
        { text: 'Inicio', value: 'start_time', sortable: true },
        { text: 'Fin', value: 'end_time', sortable: true },
        { text: 'Estado', value: 'is_active', sortable: false },
        { text: 'Ubicación', value: 'location', sortable: false }
      ],
      search: '',
      refreshInterval: null
    }
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', [
      'isSecurityLoading',
      'getSecurityError',
      'getActiveShifts'
    ]),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    shifts() {
      return this.getActiveShifts
    },
    totalShifts() {
      return this.shifts.length
    },
    guardsOnDuty() {
      return this.shifts.filter(s => s.is_active).length
    },
    locationsCovered() {
      const locations = new Set(this.shifts.map(s => s.location).filter(l => l))
      return locations.size
    }
  },
  methods: {
    fetchShifts() {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_ACTIVE_SHIFTS}`, token)
    },
    formatTime(timeString) {
      if (!timeString) return 'N/A'
      const date = new Date(timeString)
      return date.toLocaleTimeString(this.$i18n.locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    },
    startAutoRefresh() {
      // Auto-refresh every 30 seconds
      this.refreshInterval = setInterval(() => {
        this.fetchShifts()
      }, 30000)
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }
    if (this.getProfile.roll !== 8) {
      this.$router.push('/')
      return
    }
    this.fetchShifts()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  }
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 20px;
}

.mt-5 {
  margin-top: 30px;
}

.text-center {
  text-align: center;
}
</style>
